// ===============================================================
// ProBank — Admin: Virtual Card Applications & Activations
//
// Tabs:
//   Pending     → new applications awaiting fee confirmation
//   Activations → cards where the user tapped "Activate" —
//                 support switches them on
//   All         → every card in the system
//
// Uses the adminToken (same as AdminUsers/AdminDashboard), NOT
// the regular user API.
// ===============================================================

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const CardAPI = axios.create({
  baseURL: "https://alkebulanfkalertbackend.onrender.com/api/cards",
});
CardAPI.interceptors.request.use((req) => {
  const token = localStorage.getItem("adminToken");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

const TIER_BADGES = {
  classic: "bg-sky-100 text-sky-700 border-sky-300",
  gold: "bg-amber-100 text-amber-700 border-amber-300",
  platinum: "bg-slate-200 text-slate-700 border-slate-400",
};

const STATUS_BADGES = {
  pending: "bg-amber-100 text-amber-700 border-amber-300",
  approved: "bg-indigo-100 text-indigo-700 border-indigo-300",
  activation_requested: "bg-sky-100 text-sky-700 border-sky-300",
  active: "bg-emerald-100 text-emerald-700 border-emerald-300",
  frozen: "bg-sky-100 text-sky-700 border-sky-300",
  rejected: "bg-red-100 text-red-700 border-red-300",
};

const STATUS_LABELS = {
  pending: "PENDING",
  approved: "AWAITING ACTIVATION",
  activation_requested: "ACTIVATE NOW",
  active: "ACTIVE",
  frozen: "FROZEN",
  rejected: "REJECTED",
};

export default function AdminCards() {
  const [tab, setTab] = useState("pending"); // "pending" | "activations" | "all"
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const fetchCards = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint =
        tab === "pending"
          ? "/admin/pending"
          : tab === "activations"
          ? "/admin/activations"
          : "/admin/all";
      const res = await CardAPI.get(endpoint);
      setCards(res.data?.cards || []);
    } catch (err) {
      console.error("Admin cards fetch error:", err);
      showToast(err.response?.data?.message || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const handleApprove = async (cardId) => {
    if (!window.confirm("Approve this card? Only do this if the payment has arrived.")) return;
    setBusyId(cardId);
    try {
      await CardAPI.post(`/admin/approve/${cardId}`);
      showToast("Card approved — user can now request activation");
      fetchCards();
    } catch (err) {
      showToast(err.response?.data?.message || "Approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleActivate = async (cardId) => {
    if (!window.confirm("Activate this card? The user will be notified immediately.")) return;
    setBusyId(cardId);
    try {
      await CardAPI.post(`/admin/activate/${cardId}`);
      showToast("Card activated — user notified");
      fetchCards();
    } catch (err) {
      showToast(err.response?.data?.message || "Activation failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (cardId) => {
    if (!window.confirm("Reject this application?")) return;
    setBusyId(cardId);
    try {
      await CardAPI.post(`/admin/reject/${cardId}`);
      showToast("Application rejected");
      fetchCards();
    } catch (err) {
      showToast(err.response?.data?.message || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

  const TABS = [
    { key: "pending", label: "Pending Approval" },
    { key: "activations", label: "Activations" },
    { key: "all", label: "All Cards" },
  ];

  return (
    <div className="text-gray-900">
      <div className="max-w-3xl mx-auto">
        {/* header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Card Applications</h1>
            <p className="text-gray-500 text-sm mt-1">
              Approve only after confirming the fee payment arrived. Activate cards after the
              user requests activation.
            </p>
          </div>
          <button
            onClick={fetchCards}
            className="px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition"
          >
            ↻ Refresh
          </button>
        </div>

        {/* tabs */}
        <div className="flex gap-2 mt-5 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                tab === t.key
                  ? "bg-gray-900 text-white"
                  : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* list */}
        <div className="mt-5 space-y-4">
          {loading ? (
            <p className="text-gray-400 text-center py-12 animate-pulse">Loading applications...</p>
          ) : cards.length === 0 ? (
            <div className="rounded-2xl bg-white border border-gray-200 py-14 text-center">
              <div className="text-3xl mb-2">✅</div>
              <p className="text-gray-400 text-sm">
                {tab === "pending"
                  ? "No pending applications"
                  : tab === "activations"
                  ? "No activation requests"
                  : "No cards yet"}
              </p>
            </div>
          ) : (
            cards.map((card) => {
              const u = card.userId || {};
              const tierKey = card.tier || "classic";
              const feeSym = card.feeCurrency === "GHS" ? "GH₵" : "₦";

              return (
                <div
                  key={card._id}
                  className="rounded-2xl bg-white border border-gray-200 shadow-sm p-5"
                >
                  {/* top row: user + tier */}
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-bold text-base truncate">{u.name || "Unknown user"}</div>
                      <div className="text-gray-500 text-xs truncate">{u.email || "—"}</div>
                      {u.phone && (
                        <div className="text-gray-500 text-xs">{u.phone}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={`px-3 py-1 rounded-full text-[11px] font-bold border ${TIER_BADGES[tierKey] || TIER_BADGES.classic}`}
                      >
                        {tierKey.toUpperCase()}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-[11px] font-bold border ${STATUS_BADGES[card.status] || STATUS_BADGES.pending}`}
                      >
                        {STATUS_LABELS[card.status] || "PENDING"}
                      </span>
                    </div>
                  </div>

                  {/* details grid */}
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-gray-400 text-[10px] tracking-wider">NAME ON CARD</div>
                      <div className="font-semibold mt-0.5 truncate">{card.cardholderName || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-[10px] tracking-wider">FEE TO COLLECT</div>
                      <div className="font-bold text-amber-600 mt-0.5">
                        {feeSym}{(card.feeAmount || 0).toLocaleString()}
                        <span className="text-gray-400 font-normal text-xs ml-1">
                          (${card.feeUSD || 0})
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-[10px] tracking-wider">CARD NUMBER</div>
                      <div className="font-mono text-xs mt-0.5">{card.maskedNumber || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-[10px] tracking-wider">
                        {card.status === "activation_requested" ? "REQUESTED" : "APPLIED"}
                      </div>
                      <div className="text-xs mt-0.5">
                        {fmtDate(
                          card.status === "activation_requested"
                            ? card.activationRequestedAt
                            : card.appliedAt
                        )}
                      </div>
                    </div>
                  </div>

                  {/* actions */}
                  {card.status === "pending" && (
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => handleApprove(card._id)}
                        disabled={busyId === card._id}
                        className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm active:scale-[0.98] transition disabled:opacity-50"
                      >
                        {busyId === card._id ? "Working..." : "✓ Approve (payment received)"}
                      </button>
                      <button
                        onClick={() => handleReject(card._id)}
                        disabled={busyId === card._id}
                        className="px-6 py-3 rounded-xl bg-red-50 border border-red-300 text-red-600 font-semibold text-sm hover:bg-red-100 transition disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  )}

                  {card.status === "activation_requested" && (
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => handleActivate(card._id)}
                        disabled={busyId === card._id}
                        className="flex-1 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-bold text-sm active:scale-[0.98] transition disabled:opacity-50"
                      >
                        {busyId === card._id ? "Working..." : "⚡ Activate Card"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* toast */}
      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-8 z-50 px-5 py-3 rounded-full bg-gray-900 text-white border border-gray-700 text-sm shadow-2xl whitespace-nowrap">
          {toast}
        </div>
      )}
    </div>
  );
}
